import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmDialog from "./ConfirmDialog";
import GooglePlaceSearchModal from "./GooglePlaceSearchModal";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import {
  fetchGoogleCandidates,
  linkGooglePlace,
  PLACE_ID_HELP_URL,
  unlinkGooglePlace,
} from "../services/googlePlaceService";

/**
 * "Google Business Profile" — the branch's Google Maps place, so customers can
 * be invited to review it on Google after reviewing it here.
 *
 * Optional and never in the way of saving the branch:
 * - `value` / `onChange` is the manual field, sent with the branch's own save as
 *   `google_place_id`. Left empty, the API tries the map link, silently.
 * - On create (`branch` null) "Find on Google" just fills that field.
 * - On edit, Find → Confirm and Unlink act at once (`onBranchChange` gets the
 *   saved branch back).
 */
export default function GooglePlaceCard({
  branch = null,
  value,
  onChange,
  error,
  searchQuery = "",
  lat,
  lng,
  canEdit = true,
  onBranchChange,
}) {
  const { t } = useTranslation();
  const linked = Boolean(branch?.google_place_id);

  // On create there is no branch payload to read the flag from, so ask once.
  const [searchEnabled, setSearchEnabled] = useState(
    branch ? Boolean(branch.google_places_search_enabled) : false
  );
  const [finding, setFinding] = useState(false);
  const [picked, setPicked] = useState(null);
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  useEffect(() => {
    if (branch) {
      setSearchEnabled(Boolean(branch.google_places_search_enabled));
      return undefined;
    }

    let cancelled = false;
    fetchGoogleCandidates()
      .then((result) => !cancelled && setSearchEnabled(Boolean(result.enabled)))
      .catch(() => !cancelled && setSearchEnabled(false));
    return () => {
      cancelled = true;
    };
  }, [branch]);

  const confirmCandidate = async (candidate) => {
    setActionError("");

    if (!branch) {
      // Create: nothing to link yet — the id rides along with the new branch.
      setPicked(candidate);
      onChange(candidate.place_id);
      setFinding(false);
      return;
    }

    setBusy(true);
    try {
      onBranchChange?.(await linkGooglePlace(branch.id, candidate.place_id));
      setFinding(false);
      setChanging(false);
      onChange("");
    } catch (err) {
      setActionError(apiFieldErrors(err).place_id?.[0] ?? apiErrorMessage(err, t("googlePlace.linkFailed")));
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    setActionError("");
    try {
      onBranchChange?.(await unlinkGooglePlace(branch.id));
      setConfirmUnlink(false);
    } catch (err) {
      setActionError(apiErrorMessage(err, t("googlePlace.unlinkFailed")));
    } finally {
      setBusy(false);
    }
  };

  const showPicker = canEdit && (!linked || changing);

  return (
    <div className="rounded-xl border border-line p-4 md:col-span-2">
      <h3 className="text-sm font-semibold">{t("googlePlace.title")}</h3>
      <p className="mt-1 text-xs text-ink-2">{t("googlePlace.hint")}</p>

      {linked && (
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3 rounded-lg bg-raised px-3 py-2">
          <div className="min-w-0">
            <p className="font-medium">{branch.google_place_name ?? t("googlePlace.unverified")}</p>
            {branch.google_place_address && (
              <p className="text-xs text-ink-2">{branch.google_place_address}</p>
            )}
            <p className="mt-1 break-all text-xs text-ink-2" dir="ltr">
              {branch.google_place_id}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <a
              href={branch.google_review_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-accent-text hover:underline"
            >
              {t("googlePlace.openReview")}
            </a>
            {canEdit && (
              <>
                <button
                  type="button"
                  className="font-medium text-accent-text hover:underline"
                  onClick={() => setChanging((v) => !v)}
                >
                  {t("googlePlace.change")}
                </button>
                <button
                  type="button"
                  className="font-medium text-danger-text hover:underline"
                  onClick={() => setConfirmUnlink(true)}
                >
                  {t("googlePlace.unlink")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {actionError && !finding && (
        <div className="mt-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {actionError}
        </div>
      )}

      {showPicker && (
        <div className="mt-3 space-y-3">
          {searchEnabled && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-secondary"
                disabled={!searchQuery.trim()}
                onClick={() => {
                  setActionError("");
                  setFinding(true);
                }}
              >
                {t("googlePlace.find")}
              </button>
              {!searchQuery.trim() && (
                <span className="text-xs text-ink-2">{t("googlePlace.findNeedsName")}</span>
              )}
            </div>
          )}

          {picked && value === picked.place_id && (
            <div className="rounded-lg bg-raised px-3 py-2 text-sm">
              <p className="font-medium">{picked.name}</p>
              {picked.address && <p className="text-xs text-ink-2">{picked.address}</p>}
            </div>
          )}

          <div>
            <label className="field-label" htmlFor="googlePlaceId">
              {t("googlePlace.manualLabel")}
            </label>
            <input
              id="googlePlaceId"
              dir="ltr"
              className="field-input"
              value={value ?? ""}
              placeholder={t("googlePlace.manualPlaceholder")}
              onChange={(e) => {
                setPicked(null);
                onChange(e.target.value);
              }}
            />
            {error ? (
              <p className="field-error">{error}</p>
            ) : (
              !linked && !value && <p className="mt-1 text-xs text-ink-2">{t("googlePlace.autoDetect")}</p>
            )}
            <a
              href={PLACE_ID_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-block text-xs font-medium text-accent-text hover:underline"
            >
              {t("googlePlace.help")}
            </a>
          </div>
        </div>
      )}

      <GooglePlaceSearchModal
        open={finding}
        initialQuery={searchQuery}
        lat={lat}
        lng={lng}
        busy={busy}
        error={finding ? actionError : ""}
        onConfirm={confirmCandidate}
        onClose={() => setFinding(false)}
      />

      <ConfirmDialog
        open={confirmUnlink}
        title={t("googlePlace.unlinkTitle")}
        message={t("googlePlace.unlinkMessage")}
        confirmLabel={t("googlePlace.unlink")}
        busy={busy}
        error={confirmUnlink ? actionError : ""}
        onConfirm={unlink}
        onClose={() => setConfirmUnlink(false)}
      />
    </div>
  );
}
